import type { Container } from "pixi.js";
import { NULL_LOG, type Log } from "../../log/Log";
import { SymbolView } from "./SymbolView";
import type { SymbolDefinition, SymbolDefinitionResolver } from "../../symbols/SymbolDefinition";
import type { ComputedGridLayout } from "./GridLayout";

/** Context for a symbolIdHook bind. `transient` = a non-resting bind (drum lane / filler). */
export interface SymbolBindOpts {
  transient?: boolean;
  /**
   * A NON-resting bind that is nonetheless the cell's real result — a reel drum lane holding a
   * FINAL symbol, which will scroll into place and land. Lets a hook present the symbol in its
   * pre-land pose (e.g. frame 0 of a land clip) instead of the plain sprite, so the cell never
   * flips representation between arriving and landing. Ignored when `transient` is true.
   */
  pendingLand?: boolean;
}

/** Creates a fresh symbol view. */
export type SymbolViewFactory = () => SymbolView;

export class GridSymbolPool {
  // A view is pooled (`pooled`, mirrored by `pooledSet`) or in use, never both, and pooled once:
  // `release` pools only a view it has not pooled, after taking it out of `inUse`; `acquire` pops it back.
  private readonly pooled: SymbolView[] = [];
  private readonly pooledSet: Set<SymbolView> = new Set();
  private readonly inUse: Set<SymbolView> = new Set();

  private readonly symbolsByIndex: Map<number, SymbolView>;

  private symbolFactory: SymbolViewFactory | null = null;
  private log: Log = NULL_LOG;
  private parentNode: Container | null = null;

  private resolver: SymbolDefinitionResolver | null = null;
  private layout: ComputedGridLayout | null = null;

  // Optional hook fired after every applySymbolIdToView. Game-side wires
  // it to delegate to per-view extensions without the pool needing to know
  // about game-specific types. `opts.transient` marks a NON-resting bind
  // (e.g. a scrolling drum lane) so a hook can skip resting-only concerns.
  private symbolIdHook: ((view: SymbolView, symbolId: string, opts?: SymbolBindOpts) => void) | null = null;

  // Optional hook fired at the START of every release (before the view is pooled
  // or destroyed) — the teardown mirror of the symbolIdHook. Game-side wires it
  // to detach per-view overlays (e.g. an idle Spine) while the view is still valid.
  private releaseHook: ((view: SymbolView) => void) | null = null;

  public constructor(symbolsByIndex: Map<number, SymbolView>) {
    this.symbolsByIndex = symbolsByIndex;
  }

  public setSymbolIdHook(hook: (view: SymbolView, symbolId: string, opts?: SymbolBindOpts) => void): void {
    this.symbolIdHook = hook;
  }

  public setReleaseHook(hook: (view: SymbolView) => void): void {
    this.releaseHook = hook;
  }

  public configure(input: {
    symbolFactory: SymbolViewFactory | null;
    log?: Log;
    parentNode: Container;
    resolver: SymbolDefinitionResolver | null;
    layout: ComputedGridLayout;
  }): void {
    this.symbolFactory = input.symbolFactory;
    this.log = input.log ?? NULL_LOG;
    this.parentNode = input.parentNode;
    this.resolver = input.resolver;
    this.layout = input.layout;
  }

  public getOrCreate(index: number): SymbolView {
    const existing = this.symbolsByIndex.get(index);
    if (existing) return existing;

    const view = this.acquire();
    this.symbolsByIndex.set(index, view);
    return view;
  }

  public acquire(): SymbolView {
    const pooled = this.pooled.pop();
    if (pooled) {
      this.pooledSet.delete(pooled);
      this.inUse.add(pooled);

      pooled.node.visible = true;
      pooled.node.scale.set(1, 1);
      this.applyCellSize(pooled);

      return pooled;
    }

    const view = this.instantiateNew();
    this.inUse.add(view);
    view.node.visible = true;

    return view;
  }

  public release(view: SymbolView, reason: string = "unknown"): void {
    if (this.pooledSet.has(view)) {
      this.log(`[GridSymbolPool] duplicate release ignored: view already in pooled set node=${view.node.label} symbol=${view.getSymbolId?.() ?? "?"} reason=${reason}`);
      return;
    }

    // Teardown mirror of the symbolIdHook — fires while the view is still valid,
    // BEFORE pooling/destroy, so per-view overlays (e.g. an idle Spine) detach cleanly.
    this.releaseHook?.(view);

    if (!this.inUse.has(view)) {
      throw new Error(
        `[GridSymbolPool] invalid release: view is not marked inUse ` +
          `node=${view.node.uid} symbol=${view.getSymbolId?.() ?? null} reason=${reason}`,
      );
    }

    if (!view.isPoolReusable()) {
      this.inUse.delete(view);
      view.destroy();
      return;
    }

    view.node.visible = false;
    view.node.scale.set(1, 1);

    this.inUse.delete(view);
    this.pooled.push(view);
    this.pooledSet.add(view);
  }

  public releaseAllCurrent(reason: string = "release_all_current"): void {
    const current = Array.from(this.symbolsByIndex.values());

    for (const v of current) {
      this.release(v, reason);
    }

    this.symbolsByIndex.clear();
  }

  public applySymbolIdToView(view: SymbolView, symbolId: string, opts?: SymbolBindOpts): void {
    this.applyCellSize(view);
    view.setSymbolId(symbolId);
    this.symbolIdHook?.(view, symbolId, opts);
  }

  /**
   * Resolve a symbol id to its definition via the injected resolver, WITHOUT a
   * bound view. Used on land paths where the grid isn't index-bound yet (the reel
   * binds `symbolsByIndex` only after all reels stop, so a per-column land-time
   * view scan is empty). Returns null when no resolver is wired or the id is
   * unknown.
   */
  public resolveDefinition(symbolId: string): SymbolDefinition | null {
    return this.resolver?.resolveDefinition(symbolId) ?? null;
  }

  /** Number of views currently bound or otherwise checked out (tests / leak checks). */
  public getInUseCount(): number {
    return this.inUse.size;
  }

  public getPooledCount(): number {
    return this.pooled.length;
  }

  private applyCellSize(view: SymbolView): void {
    if (!this.layout) return;
    view.setCellSizeForFit(this.layout.cellWidth, this.layout.cellHeight);
  }

  private instantiateNew(): SymbolView {
    if (!this.parentNode) throw new Error("GridSymbolPool: parentNode is not assigned.");

    const view = this.symbolFactory ? this.symbolFactory() : new SymbolView(this.log);
    this.parentNode.addChild(view.node);

    if (this.resolver) view.setDefinitionResolver(this.resolver);
    this.applyCellSize(view);

    view.node.scale.set(1, 1);
    view.node.visible = true;

    return view;
  }
}
